import JsPDF from 'jspdf';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { useTranslation } from 'react-i18next';
import type { CashList } from '@pflegenavi/frontend/api-nursing-home';
import { CashListStorageType } from '@pflegenavi/shared/api';
import type {
  Context,
  HandlePrintAccountingOverviewResult,
} from './handlePrintPdfAccountingOverview';
import {
  BASELINE_BOTTOM_MARGIN,
  BASELINE_LEFT_MARGIN,
  BASELINE_RIGHT_MARGIN,
  BASELINE_TOP_MARGIN,
  drawCashOverview,
  drawDate,
  drawFooters,
  drawHeaders,
  drawNursingHome,
  drawSignatureField,
} from './handlePrintPdfAccountingOverview';

interface HandlePrintProps {
  nursingHomeName: string;
  logo?: string;
  date: Date;
  // The institution cash list with its balance as of `date`
  cashList: CashList;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const usePrintPdfFinanceOverviewAtDate = () => {
  const { t } = useTranslation();
  const { fCurrency, fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();

  const handlePrintPdfFinanceOverviewAtDate = async ({
    nursingHomeName,
    logo,
    date,
    cashList,
  }: HandlePrintProps): Promise<HandlePrintAccountingOverviewResult> => {
    const doc = new JsPDF();

    const context: Context = {
      t,
      fCurrency: (amount, opts) =>
        fCurrency(amount, { ...opts, useNonBreakingCharacters: false }),
      fCurrencyCents: (amount) =>
        fCurrencyCents(amount, { useNonBreakingCharacters: false }),
      fDate,
    };

    let positionY = drawNursingHome(
      context,
      doc,
      BASELINE_TOP_MARGIN,
      nursingHomeName
    );
    drawDate(context, doc, BASELINE_TOP_MARGIN, date);

    positionY += 5; // Add some spacing

    const cashSum = cashList.coins.reduce(
      (acc, value) => acc + value.amount * value.factor,
      0
    );
    const sortedCoins = [...cashList.coins].sort((a, b) => b.factor - a.factor);

    positionY = drawCashOverview(context, doc, positionY, {
      cashListName: cashList.name ?? t('accounting.dashboard.cash-title'),
      cashAmounts: sortedCoins,
      cashSum,
      cashBankAccountAmountInCents: cashList.bankAccountAmountInCent,
      supportsSavings:
        cashList.storageType ===
        (CashListStorageType.Cash | CashListStorageType.BankAccount),
    });

    // Page break if the signatures do not fit on the same page
    if (
      positionY + 25 >
      doc.internal.pageSize.height - BASELINE_BOTTOM_MARGIN
    ) {
      doc.addPage();
      positionY = BASELINE_TOP_MARGIN;
    }

    drawSignatureField(
      context,
      doc,
      BASELINE_LEFT_MARGIN + 1.5,
      positionY + 25,
      60,
      t('accounting.report.signature-employee')
    );

    drawSignatureField(
      context,
      doc,
      doc.internal.pageSize.width - BASELINE_RIGHT_MARGIN - 1.5 - 60,
      positionY + 25,
      60,
      t('accounting.report.signature-supervisor')
    );

    await drawHeaders(context, doc, {
      logo,
    });

    drawFooters(context, doc);

    // Get the PDF data as a Blob
    const pdfBlob = doc.output('blob');

    // Create a URL from the Blob
    const url = URL.createObjectURL(pdfBlob);

    return {
      blob: pdfBlob,
      save: doc.save,
      url,
      date,
    };
  };

  return {
    handlePrintPdfFinanceOverviewAtDate,
  };
};
